import React from 'react';
import { cn } from '@/lib/utils';
import { FormattedMarkdown } from '@/utils/markdownFormatter';
import { useScrollFadeMask } from '@/hooks/useScrollFade';

interface HelpSheetBodyProps {
  fieldName: string;
  content: string;
  variables?: Record<string, unknown>;
  injectableComponents?: Record<string, React.ReactNode>;
  enableFieldFocus?: boolean;
  headerLeading?: React.ReactNode;
  contentVisible?: boolean;
  debugLayout?: boolean;
  measureGranularity?: 'coarse' | 'fine' | 'micro' | 'dom';
  enableMeasureTextTargets?: boolean;
  expandToContent?: boolean;
}

export const HelpSheetBody: React.FC<HelpSheetBodyProps> = ({
  fieldName,
  content,
  variables,
  injectableComponents,
  enableFieldFocus = false,
  headerLeading,
  contentVisible = true,
  debugLayout = false,
  measureGranularity = 'coarse',
  enableMeasureTextTargets = false,
  expandToContent = false,
}) => {
  // Skip the ResizeObserver/scroll listener work entirely when the body
  // is sized to its content (no scrolling possible). React hook rules
  // force us to still call the hook, so the opt-out lives in `enabled`.
  const scrollFadeRef = useScrollFadeMask({ enabled: !expandToContent });

  return (
    <div
      className={cn(
        'flex flex-col transition-opacity duration-300',
        !expandToContent && 'flex-1 min-h-0',
        contentVisible ? 'opacity-100' : 'opacity-0'
      )}
    >
      {/* Plain HTML instead of SheetHeader/SheetTitle so this renders
          outside a Radix Sheet context (e.g. standalone audit page).
          SheetHeader = div with "flex flex-col space-y-2 text-center sm:text-left"
          SheetTitle  = h2 with "text-lg font-semibold text-foreground" */}
      <div
        data-measure-label="Help Header"
        data-auto-gap-node="Help Header"
        className={cn(
          'flex flex-col space-y-2 text-center sm:text-left relative pb-3 mb-2',
          debugLayout && 'outline outline-fuchsia-300/60 rounded-[2px] bg-fuchsia-500/[0.04]',
        )}
      >
        {debugLayout && (
          <div className="pointer-events-none absolute -top-2 right-1 px-1 py-0.5 rounded bg-fuchsia-400/20 border border-fuchsia-300/60 text-[9px] font-mono uppercase tracking-[0.06em] text-fuchsia-100">
            Header Zone
          </div>
        )}
        {headerLeading}
        <h2 className="text-base leading-none font-semibold text-foreground font-heading uppercase tracking-[0.08em]">
          {fieldName}
        </h2>
        <div
          aria-hidden="true"
          data-measure-label="Help Header Rule"
          className="absolute left-0 right-0 -bottom-1 h-2"
        >
          <div className="pointer-events-none absolute left-0 right-0 top-1/2 -translate-y-1/2 h-px bg-[var(--border)]" />
        </div>
        <div
          aria-hidden="true"
          data-measure-label="Help Header Accent Rule"
          className="absolute left-0 -bottom-1 h-2 w-10"
        >
          <div className="pointer-events-none absolute left-0 top-1/2 -translate-y-1/2 h-[2px] w-full bg-primary/60" />
        </div>
      </div>
      <div
        ref={expandToContent ? undefined : scrollFadeRef}
        data-measure-label="Help Content"
        data-auto-gap-node="Help Content"
        className={cn(
          expandToContent
            ? 'relative overflow-visible'
            : 'relative overflow-y-auto overflow-x-hidden flex-1 min-h-0 scroll-fade-y',
          debugLayout && 'outline outline-emerald-300/60 rounded-[2px] bg-emerald-500/[0.04]',
        )}
      >
        {debugLayout && (
          <>
            <div
              className="pointer-events-none absolute inset-0 z-0"
              style={{
                backgroundImage: 'linear-gradient(to bottom, rgba(52, 211, 153, 0.14) 1px, transparent 1px)',
                backgroundSize: '100% 8px',
              }}
            />
            <div className="pointer-events-none absolute top-1 right-1 z-[1] px-1 py-0.5 rounded bg-emerald-400/20 border border-emerald-300/60 text-[9px] font-mono uppercase tracking-[0.06em] text-emerald-100">
              Content Zone
            </div>
          </>
        )}
        <div className={cn(debugLayout && 'relative z-[1]')}>
        <FormattedMarkdown
          content={content}
          variables={variables}
          enableFieldFocus={enableFieldFocus}
          injectableComponents={injectableComponents}
          measureGranularity={measureGranularity}
          enableMeasureTextTargets={enableMeasureTextTargets}
        />
        </div>
      </div>
    </div>
  );
};
